import { z } from 'zod';
import { AccessType, DeviceOrganization, OrganizationRef, SessionAuthMethod, UserRef } from './org.js';
import { DeviceId, IceServer, SessionEndReason, SessionId, SessionRole } from './session.js';
export const PROTOCOL_VERSION = 1;
/** Código de fechamento do WebSocket quando a mesma identidade conectou em outro lugar. Não reconectar. */
export const REPLACED_CLOSE_CODE = 4000;
/* ------------------------------------------------------------------ */
/* Payload de signaling WebRTC (repassado sem interpretação pelo servidor) */
/* ------------------------------------------------------------------ */
export const SdpPayload = z.object({
    kind: z.enum(['offer', 'answer']),
    sdp: z.string().min(1),
});
export const IcePayload = z.object({
    kind: z.literal('ice'),
    candidate: z.string(),
    sdpMid: z.string().nullable(),
    sdpMLineIndex: z.number().int().nonnegative().nullable(),
});
export const SignalPayload = z.discriminatedUnion('kind', [SdpPayload, IcePayload]);
/* ------------------------------------------------------------------ */
/* Cliente -> Servidor                                                  */
/* ------------------------------------------------------------------ */
const DeviceName = z.string().min(1).max(64);
export const RegisterRequest = z.object({
    type: z.literal('register'),
    protocolVersion: z.literal(PROTOCOL_VERSION),
    name: DeviceName,
});
export const Hello = z.object({
    type: z.literal('hello'),
    protocolVersion: z.literal(PROTOCOL_VERSION),
    deviceId: DeviceId,
    deviceSecret: z.string().min(32).max(128),
    name: DeviceName,
    /** `false` no navegador: só acessa outros computadores. Ausente equivale a `true`. */
    canHost: z.boolean().optional(),
    /** Login do usuário (API REST). Necessário para acessar dispositivos de uma empresa. */
    userToken: z.string().min(1).max(256).optional(),
});
export const SessionRequest = z.object({
    type: z.literal('session_request'),
    targetId: DeviceId,
});
export const SessionAccept = z.object({
    type: z.literal('session_accept'),
    sessionId: SessionId,
    /** Presente quando o Host aceitou porque a senha de acesso estava certa (só para o histórico). */
    via: z.literal('password').optional(),
});
export const SessionReject = z.object({
    type: z.literal('session_reject'),
    sessionId: SessionId,
});
export const SessionEnd = z.object({
    type: z.literal('session_end'),
    sessionId: SessionId,
});
export const SignalSend = z.object({
    type: z.literal('signal'),
    sessionId: SessionId,
    token: z.string().min(1),
    payload: SignalPayload,
});
/** Host avisa se aceita senha de acesso; o servidor só repassa o fato ao Viewer, nunca vê o hash. */
export const HostOptions = z.object({
    type: z.literal('host_options'),
    passwordEnabled: z.boolean(),
});
const AccessPassword = z.string().min(1).max(128);
/**
 * Viewer -> Host durante o pedido pendente, repassada pelo servidor sem alteração.
 * Quem confere é o Host.
 */
export const SessionPassword = z.object({
    type: z.literal('session_password'),
    sessionId: SessionId,
    password: AccessPassword,
});
/** Host -> Viewer quando a senha está errada. Senha certa vira `session_accept`. */
export const SessionPasswordRejected = z.object({
    type: z.literal('session_password_rejected'),
    sessionId: SessionId,
    attemptsLeft: z.number().int().nonnegative(),
});
/** Aplicativo do PC digita o código gerado no painel da empresa. */
export const EnrollDevice = z.object({
    type: z.literal('enroll_device'),
    code: z.string().trim().min(1).max(32),
});
/** Quem está no PC tira uma empresa dele; ela só volta com um novo código de vinculação. */
export const LeaveOrganization = z.object({
    type: z.literal('leave_organization'),
    organizationId: z.string().uuid(),
});
/**
 * Quem está no PC libera ou retira o acesso sem supervisão de uma empresa. Só o próprio
 * computador manda isto, e só para ele mesmo: a empresa não tem como se liberar.
 */
export const SetUnattended = z.object({
    type: z.literal('set_unattended'),
    organizationId: z.string().uuid(),
    allowed: z.boolean(),
});
export const ClientMessage = z.discriminatedUnion('type', [
    RegisterRequest,
    Hello,
    SessionRequest,
    SessionAccept,
    SessionReject,
    SessionEnd,
    SignalSend,
    HostOptions,
    SessionPassword,
    SessionPasswordRejected,
    EnrollDevice,
    LeaveOrganization,
    SetUnattended,
]);
/* ------------------------------------------------------------------ */
/* Servidor -> Cliente                                                  */
/* ------------------------------------------------------------------ */
export const Registered = z.object({
    type: z.literal('registered'),
    deviceId: DeviceId,
    deviceSecret: z.string(),
});
export const Welcome = z.object({
    type: z.literal('welcome'),
    deviceId: DeviceId,
    /** Usuário reconhecido pelo `userToken`; `null` sem login ou com token vencido. */
    user: UserRef.nullable().optional(),
    /** Empresas que gerenciam este dispositivo; vazio no modo avulso. */
    organizations: z.array(DeviceOrganization).optional(),
});
/**
 * Servidor avisa que as empresas do dispositivo mudaram (vinculação, remoção, renomeação,
 * acesso sem supervisão liberado ou usado).
 */
export const DeviceOrganizations = z.object({
    type: z.literal('device_organizations'),
    organizations: z.array(DeviceOrganization),
});
/** Host recebe um pedido de conexão. */
export const SessionIncoming = z.object({
    type: z.literal('session_incoming'),
    sessionId: SessionId,
    viewerId: DeviceId,
    viewerName: z.string(),
    expiresAt: z.string().datetime(),
    /** Colaborador logado que pediu a conexão (dispositivos de empresa). */
    viewerUser: UserRef.nullable().optional(),
    /** O que foi concedido pela empresa; ausente no modo avulso (controle total). */
    access: AccessType.optional(),
    /**
     * Área de transferência e arquivos. Ausentes no modo avulso, que libera os dois.
     * No caminho da empresa, seguem as permissões `clipboard` e `file_transfer`.
     */
    clipboard: z.boolean().optional(),
    fileTransfer: z.boolean().optional(),
    /** Empresa pela qual o colaborador pede o acesso. */
    organization: OrganizationRef.optional(),
    /** Acesso não assistido: se ninguém recusar até este horário, o servidor inicia a sessão. */
    autoAcceptAt: z.string().datetime().optional(),
    /** Horário do servidor no envio, para o Host descontar a diferença do próprio relógio nos prazos. */
    sentAt: z.string().datetime().optional(),
});
/** Viewer é avisado de que o pedido chegou ao Host. */
export const SessionPending = z.object({
    type: z.literal('session_pending'),
    sessionId: SessionId,
    expiresAt: z.string().datetime(),
    /** O Host tem senha de acesso: o Viewer pode digitá-la em vez de esperar o aceite. */
    passwordEnabled: z.boolean(),
    /** Acesso não assistido: a sessão começa sozinha neste horário se ninguém recusar no Host. */
    autoAcceptAt: z.string().datetime().optional(),
});
/** Enviado aos dois lados quando o Host aceita. */
export const SessionStarted = z.object({
    type: z.literal('session_started'),
    sessionId: SessionId,
    role: SessionRole,
    token: z.string().min(1),
    peerId: DeviceId,
    peerName: z.string(),
    iceServers: z.array(IceServer),
    expiresAt: z.string().datetime(),
    /** Ausente no modo avulso, que equivale a `control`. */
    access: AccessType.optional(),
    /** Ausentes no modo avulso: os dois ficam liberados. */
    clipboard: z.boolean().optional(),
    fileTransfer: z.boolean().optional(),
    authMethod: SessionAuthMethod.optional(),
    /** Só para o Host: colaborador e empresa do pedido, para listar quem está conectado. */
    viewerUser: UserRef.nullable().optional(),
    organization: OrganizationRef.optional(),
});
export const SessionRejected = z.object({
    type: z.literal('session_rejected'),
    sessionId: SessionId,
});
export const SessionEnded = z.object({
    type: z.literal('session_ended'),
    sessionId: SessionId,
    reason: SessionEndReason,
});
export const SignalReceive = z.object({
    type: z.literal('signal'),
    sessionId: SessionId,
    from: DeviceId,
    payload: SignalPayload,
});
export const ErrorCode = z.enum([
    'bad_message',
    'unauthorized',
    'unknown_device',
    'device_offline',
    'device_busy',
    'viewer_only',
    'session_not_found',
    'session_forbidden',
    /** Dispositivo de empresa: quem pede não é colaborador ou não tem permissão. */
    'access_denied',
    /** Código de vinculação inexistente, vencido ou já usado. */
    'invalid_code',
    'rate_limited',
    /** A mesma identidade conectou de novo (outra aba ou janela); esta conexão vai ser fechada. */
    'replaced',
    'internal',
]);
export const ServerError = z.object({
    type: z.literal('error'),
    code: ErrorCode,
    message: z.string(),
    /** Tipo da mensagem do cliente que causou o erro, quando conhecido. */
    ref: z.string().optional(),
});
export const ServerMessage = z.discriminatedUnion('type', [
    Registered,
    Welcome,
    DeviceOrganizations,
    SessionIncoming,
    SessionPending,
    SessionPassword,
    SessionPasswordRejected,
    SessionStarted,
    SessionRejected,
    SessionEnded,
    SignalReceive,
    ServerError,
]);
//# sourceMappingURL=signaling.js.map